/**
 * Power Output Fields - Power outputs configuration
 */

import React from 'react';
import { Box, Flex, Text, TextField, Checkbox, Button, IconButton } from '@radix-ui/themes';
import { PlusIcon, Cross2Icon } from '@radix-ui/react-icons';
import { DeviceTemplate } from '../../types';

interface PowerOutputFieldsProps {
  template: Partial<DeviceTemplate>;
  onChange: (updated: Partial<DeviceTemplate>) => void;
}

const PowerOutputFields: React.FC<PowerOutputFieldsProps> = ({ template, onChange }) => {
  const powerOutputs = template.powerOutputs || [];

  return (
    <Box>
      <Flex align="center" gap="2" mb="2">
        <Checkbox
          checked={powerOutputs.length > 0}
          onCheckedChange={(checked) => {
            if (checked) {
              onChange({ ...template, powerOutputs: [{ voltage: 12, maxCurrent: 10, portCount: 1 }] });
            } else {
              onChange({ ...template, powerOutputs: [] });
            }
          }}
        />
        <Text size="2" weight="bold">
          Has Power Outputs (for power boxes/PSUs)
        </Text>
      </Flex>

      {powerOutputs.length > 0 && (
        <>
          <Box>
            <Text as="label" size="2" weight="bold" htmlFor="maxTotalCurrent">
              Total Current Limit (A)
            </Text>
            <TextField.Root
              id="maxTotalCurrent"
              type="number"
              step="0.1"
              value={template.maxTotalCurrent || ''}
              onChange={(e) => onChange({ ...template, maxTotalCurrent: parseFloat(e.target.value) || undefined })}
              placeholder="e.g., 12 (optional)"
              mt="1"
            />
            <Text as="p" size="1" color="gray" mt="1">
              Maximum total current across all outputs (leave empty if no shared limit)
            </Text>
          </Box>

          <Box p="3" style={{ backgroundColor: 'var(--gray-2)', borderRadius: 'var(--radius-2)' }}>
            <Flex justify="between" align="center" mb="2">
              <Text size="1" weight="bold">Power Output Configurations</Text>
              <Button
                type="button"
                size="1"
                variant="soft"
                onClick={() => onChange({
                  ...template,
                  powerOutputs: [...powerOutputs, { voltage: 12, maxCurrent: 10, portCount: 1 }]
                })}
              >
                <PlusIcon /> Add Output
              </Button>
            </Flex>
          
            <Flex direction="column" gap="2">
              {powerOutputs.map((output, index) => (
                <Flex key={index} gap="2" align="end">
                  <Box style={{ flex: 1 }}>
                    <Text as="label" size="1">Voltage (V)</Text>
                    <TextField.Root
                      type="number"
                      step="0.1"
                      value={output.voltage}
                      onChange={(e) => {
                        const updated = [...powerOutputs];
                        updated[index].voltage = parseFloat(e.target.value) || 0;
                        onChange({ ...template, powerOutputs: updated });
                      }}
                      mt="1"
                    />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Text as="label" size="1">Max Current (A)</Text>
                    <TextField.Root
                      type="number"
                      step="0.1"
                      value={output.maxCurrent}
                      onChange={(e) => {
                        const updated = [...powerOutputs];
                        updated[index].maxCurrent = parseFloat(e.target.value) || 0;
                        onChange({ ...template, powerOutputs: updated });
                      }}
                      mt="1"
                    />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Text as="label" size="1">Port Count</Text>
                    <TextField.Root
                      type="number"
                      min="1"
                      value={output.portCount}
                      onChange={(e) => {
                        const updated = [...powerOutputs];
                        updated[index].portCount = parseInt(e.target.value) || 1;
                        onChange({ ...template, powerOutputs: updated });
                      }}
                      mt="1"
                    />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <Text as="label" size="1">Label (optional)</Text>
                    <TextField.Root
                      value={output.label || ''}
                      onChange={(e) => {
                        const updated = [...powerOutputs];
                        updated[index].label = e.target.value;
                        onChange({ ...template, powerOutputs: updated });
                      }}
                      placeholder="e.g., 12V DC"
                      mt="1"
                    />
                  </Box>
                  {powerOutputs.length > 1 && (
                    <IconButton
                      type="button"
                      size="1"
                      variant="soft"
                      color="red"
                      onClick={() => {
                        const updated = powerOutputs.filter((_, i) => i !== index);
                        onChange({ ...template, powerOutputs: updated });
                      }}
                    >
                      <Cross2Icon />
                    </IconButton>
                  )}
                </Flex>
              ))}
            </Flex>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PowerOutputFields;
